import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Checkbox, Icon } from "@legal-500/legal500-design-system";
import { useSearchableSelect } from "../Form/SearchableSelect/useSearchableSelect";
import selectStyles from "../Form/SearchableSelect/SearchableSelect.module.css";
import styles from "./MultiSelectFilter.module.css";

export type MultiSelectFilterOption = { value: string; label: string };

export type MultiSelectFilterProps = {
  /** Used for the trigger id / option ids; unique per page. */
  name: string;
  /**
   * Rendered above the field like the other filter controls. Pass "" to render no label
   * (DataFilters does this when it draws its own label row).
   */
  label: string;
  /** Selected option values. */
  value: string[];
  /**
   * Options in the order they must be shown — the component never re-sorts them (years come DESC,
   * dictionary values by name, jurisdictions by resolved name — the caller decides).
   */
  options: MultiSelectFilterOption[];
  /** Called with the new selection, always in `options` order, never with duplicates. */
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /** Trigger text while nothing is selected, e.g. "All products". */
  allLabel: string;
};

/**
 * Checkbox multi-select for a list-page filter row: a labelled field showing the current
 * selection, opening onto a list of checkbox rows. The trigger mirrors the design-system `Select`
 * (small) with its own classes, so a multi-select sits in a filter row indistinguishable from the
 * single selects beside it; only the open panel — positioning, list and rows — is shared with
 * `SearchableSelect`. There is no search box, and chips and "Clear all" are left to `DataFilters`,
 * which already renders them for every filter.
 */
export function MultiSelectFilter({
  name,
  label,
  value,
  options,
  onChange,
  disabled = false,
  allLabel,
}: MultiSelectFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Positioning under the trigger (and above it when there is no room), repositioning on
  // scroll/resize and closing on an outside click, exactly as SearchableSelect does. The hook's
  // search, highlight and key handling go unused: there is no search box to drive them.
  const { dropdownStyle, containerRef, dropdownRef } = useSearchableSelect({
    options,
    isOpen,
    onClose: () => setIsOpen(false),
    preserveOptionOrder: true,
  });

  // The panel is portalled to the end of <body>, so the browser's own Tab order would jump from
  // the trigger straight to the next filter. Tab from the trigger hops into the first checkbox
  // instead and Shift+Tab from the first one hops back; Escape, or Tab past the last checkbox,
  // closes the panel with focus on the trigger; tabbing away from the trigger just closes it.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const trigger = triggerRef.current;
      const checkboxes = dropdownRef.current?.querySelectorAll("input") ?? [];
      const first = checkboxes[0];
      const last = checkboxes[checkboxes.length - 1];
      const active = document.activeElement;
      const tabForward = event.key === "Tab" && !event.shiftKey;

      if (tabForward && active === trigger && first) {
        event.preventDefault();
        first.focus();
      } else if (event.key === "Tab" && event.shiftKey && active === first) {
        event.preventDefault();
        trigger?.focus();
      } else if (event.key === "Escape" || (tabForward && active === last)) {
        event.preventDefault();
        setIsOpen(false);
        trigger?.focus();
      } else if (event.key === "Tab" && active === trigger) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, dropdownRef]);

  const selectedValues = new Set(value);
  // A value that is not among the options (a stale URL) is simply not shown.
  const selectedOptions = options.filter(option => selectedValues.has(option.value));
  const triggerText =
    selectedOptions.length === 0
      ? allLabel
      : selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selected`;

  const toggle = (optionValue: string) => {
    const next = new Set(selectedValues);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onChange(options.filter(option => next.has(option.value)).map(option => option.value));
  };

  // The checkbox's onChange is the only place a selection changes. The design-system Checkbox
  // draws its text as a plain <span> beside its <label>, so a click on the text or the row padding
  // would otherwise do nothing: forward it to the input, as a <label> would. Clicks that land on
  // the input or its own <label> already reach it natively and are left alone, so one click can
  // never toggle twice.
  const forwardRowClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (event.target instanceof Element && event.target.closest("input, label")) return;
    event.currentTarget.querySelector("input")?.click();
  };

  const labelId = `${name}-label`;
  const valueId = `${name}-value`;
  const listboxId = `${name}-listbox`;

  return (
    <div className={styles.field}>
      {label && (
        <label id={labelId} htmlFor={name} className={styles.label}>
          {label}
        </label>
      )}

      <div ref={containerRef} className={styles.container}>
        <button
          ref={triggerRef}
          type="button"
          id={name}
          className={styles.trigger}
          onClick={() => setIsOpen(open => !open)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-labelledby={label ? `${labelId} ${valueId}` : valueId}
        >
          <span id={valueId} className={styles.text}>
            {triggerText}
          </span>
        </button>
        <Icon
          icon="chevron-down"
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
          aria-hidden="true"
        />
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className={selectStyles.dropdown} style={dropdownStyle}>
            <ul
              id={listboxId}
              className={selectStyles.optionsList}
              role="listbox"
              aria-multiselectable="true"
              aria-label={label || allLabel}
            >
              {options.length === 0 ? (
                <li className={selectStyles.noResults}>No options</li>
              ) : (
                options.map((option, index) => {
                  const checked = selectedValues.has(option.value);
                  return (
                    <li
                      key={option.value}
                      className={selectStyles.option}
                      onClick={forwardRowClick}
                      role="option"
                      aria-selected={checked}
                    >
                      <Checkbox
                        id={`${name}-option-${index}`}
                        className={styles.checkbox}
                        label={option.label}
                        // `label` renders as an unassociated <span>; name the input explicitly
                        // for screen readers (and role-based queries).
                        aria-label={option.label}
                        checked={checked}
                        onChange={() => toggle(option.value)}
                      />
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
